import React, { useRef, useState } from "react";
import { ChevronRight, ChevronDown } from "react-feather";
import styled, { css } from "styled-components";
import Button from "./Button";
import AnimateHeight, { AnimateHeightRef } from "./AnimateHeight";
import { isEmptyNode } from "./utils";

const Wrapper = styled.div<{
  $noHover?: boolean;
  $collapsed?: boolean;
  $noPad?: boolean;
}>`
  margin: 15px 0;
  border-radius: 10px;
  border: 1px solid var(--dark500);
  transition: border 150ms;

  ${(props) =>
    !props.$noHover &&
    css`
      &:hover {
        border-color: var(--blue090);
      }
    `}
  ${(props) =>
    !props.$collapsed &&
    !props.$noPad &&
    css`
      padding-bottom: 5px;
    `};
`;

const Title = styled.div<{ $collapsed?: boolean; $noPad?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 5px 10px 5px 15px;
  border-radius: 10px;
  background-color: var(--dark300);

  ${(props) =>
    !props.$collapsed &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom: 1px solid var(--dark060);
    `}
  ${(props) =>
    !props.$collapsed &&
    !props.$noPad &&
    css`
      margin-bottom: 5px;
    `};
`;

const Buttons = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: flex-end;
  min-width: 0;
  margin-left: 10px;

  > * + * {
    margin-left: 5px;
  }
`;

const TitleText = styled.div<{ $inactive?: boolean; $collapsible?: boolean }>`
  min-height: 30px;
  font-size: 17px;
  overflow-wrap: break-word;
  overflow: hidden;
  display: flex;
  align-items: center;
  /* width: 100%; */
  padding: 4px 0;
  flex-wrap: wrap;

  ${(props) =>
    props.$inactive &&
    css`
      color: var(--dark700);
    `};

  ${(props) =>
    props.$collapsible &&
    css`
      margin-left: 2px;
    `};
`;

const TitleValue = styled.span`
  margin-right: 10px;
`;

const SubTitle = styled.span`
  font-size: 15px;
  margin-top: 2px;
  color: var(--dark700);
`;

const Body = styled.div<{ $padding?: boolean }>`
  overflow: hidden;

  ${(props) =>
    props.$padding &&
    css`
      padding: 0 15px;
    `}
`;

const CollapseButton = styled(Button)`
  margin-left: -8px;
`;

interface Props {
  collapsible?: boolean;
  collapsedChildren?: React.ReactNode;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  buttons?: React.ReactNode;
  children?: React.ReactNode;
  padHorizontal?: boolean;
  inactive?: boolean;
  hidden?: boolean;
  noHover?: boolean;
  collapsed?: boolean;
  setCollapsed?: (collapsed: boolean) => void;
  className?: string;
  noPad?: boolean;
}

const InputGroup = (props: Props) => {
  const {
    collapsible,
    collapsedChildren,
    title,
    subTitle,
    buttons,
    children,
    padHorizontal,
    inactive,
    hidden,
    noHover,
    collapsed,
    setCollapsed,
    className,
    noPad,
  } = props;
  const [internalCollapsed, internalSetCollapsed] = useState(false);
  const autoHeightRef = useRef<AnimateHeightRef>(null);

  const isCollapsed =
    (collapsed !== undefined ? collapsed : internalCollapsed) && collapsible;
  const body = isCollapsed ? collapsedChildren : children;
  const bodyEmpty = isEmptyNode(body);

  const toggleIsCollapsed = () => {
    autoHeightRef.current?.update();
    setCollapsed !== undefined
      ? setCollapsed(!isCollapsed)
      : internalSetCollapsed(!internalCollapsed);
  };

  return !hidden ? (
    <Wrapper
      $noHover={noHover}
      className={className}
      $collapsed={bodyEmpty}
      $noPad={noPad}
    >
      {(collapsible || title || subTitle || buttons) && (
        <Title $collapsed={bodyEmpty} $noPad={noPad}>
          {collapsible && (
            <CollapseButton
              icon={isCollapsed ? ChevronRight : ChevronDown}
              onClick={() => toggleIsCollapsed()}
            />
          )}
          {(title || subTitle) && (
            <TitleText $inactive={inactive} $collapsible={collapsible}>
              <TitleValue>{title}</TitleValue>
              {subTitle && <SubTitle>{subTitle}</SubTitle>}
            </TitleText>
          )}
          {buttons && <Buttons>{buttons}</Buttons>}
        </Title>
      )}
      <Body
        $padding={padHorizontal}
        as={collapsible ? AnimateHeight : "div"}
        ref={autoHeightRef}
      >
        {body}
      </Body>
    </Wrapper>
  ) : (
    <></>
  );
};

export default InputGroup;
